import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createMerchant } from '@/features/merchants/api';
import type { CreateMerchantModel } from '@/features/merchants/merchant';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { parsePhone } from '@/lib/phone';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Building2,
  DollarSign,
  Loader2,
  MapPin,
  Phone,
  ShoppingBag,
  User,
} from '@/lib/icons';
import { useToast } from '@/hooks/use-toast';

export const Route = createFileRoute('/_layout/merchants/new')({
  component: NewMerchantPage,
});

const SETTLEMENT_CHANNELS = [
  { value: 'zaad', label: 'ZAAD' },
  { value: 'edahab', label: 'eDahab' },
  { value: 'evc', label: 'EVC Plus' },
] as const;

function NewMerchantPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [channel, setChannel] = useState<string>('zaad');
  const [settlementNumber, setSettlementNumber] = useState('');
  const [city, setCity] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: (data: CreateMerchantModel) => createMerchant(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      toast({
        title: 'Merchant onboarded',
        description: `${result.value?.businessName ?? 'Merchant'} is now active.`,
      });
      if (result.value?.id) {
        navigate({
          to: '/merchants/$merchantId',
          params: { merchantId: result.value.id },
        });
      } else {
        navigate({ to: '/merchants' });
      }
    },
    onError: (err: any) => {
      toast({
        variant: 'destructive',
        title: 'Could not onboard merchant',
        description: err?.message ?? 'Please check the details and try again.',
      });
    },
  });

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!businessName.trim()) next.businessName = 'Business name is required.';
    // This number is the merchant's identity *and* their login, so a
    // near-miss here locks the owner out of their own shop. Demand a complete
    // number rather than a digit count that a half-typed one also passes.
    if (!ownerPhone.trim()) next.ownerPhone = 'Owner phone is required.';
    else if (!parsePhone(ownerPhone).isValid)
      next.ownerPhone = 'Enter a complete phone number.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const settlement = parsePhone(settlementNumber).e164 ?? settlementNumber.trim();
    const payload: CreateMerchantModel = {
      businessName: businessName.trim(),
      ownerPhone: parsePhone(ownerPhone).e164 ?? ownerPhone.trim(),
      ...(ownerName.trim() ? { ownerName: ownerName.trim() } : {}),
      ...(settlement ? { settlementAccounts: { [channel]: settlement } } : {}),
      ...(city.trim() ? { city: city.trim() } : {}),
    };
    mutation.mutate(payload);
  };

  const saving = mutation.isPending;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/merchants' })}
          aria-label="Back to merchants"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Onboard Merchant</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Business name, owner phone, and a settlement number — active immediately.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="shadow-md border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShoppingBag className="w-4 h-4 text-primary" />
              Merchant details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5 pt-4">
            {/* Business name */}
            <Field
              id="businessName"
              label="Business name"
              icon={<Building2 className="w-3.5 h-3.5" />}
              required
              error={errors.businessName}
            >
              <Input
                id="businessName"
                placeholder="e.g. Dahabi Factory"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                autoFocus
              />
            </Field>

            {/* Owner name */}
            <Field
              id="ownerName"
              label="Owner name"
              icon={<User className="w-3.5 h-3.5" />}
            >
              <Input
                id="ownerName"
                placeholder="e.g. Aisha Mohamed"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
              />
            </Field>

            {/* Owner phone */}
            <Field
              id="ownerPhone"
              label="Owner phone"
              icon={<Phone className="w-3.5 h-3.5" />}
              required
              error={errors.ownerPhone}
              hint="This is the merchant's identity and login."
            >
              <PhoneInput
                id="ownerPhone"
                value={ownerPhone}
                onChange={setOwnerPhone}
                invalid={!!errors.ownerPhone}
              />
            </Field>

            {/* Settlement */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5 text-sm">
                <DollarSign className="w-3.5 h-3.5" />
                Settlement account
              </Label>
              <div className="flex gap-2">
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="w-32 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SETTLEMENT_CHANNELS.map((ch) => (
                      <SelectItem key={ch.value} value={ch.value}>
                        {ch.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <PhoneInput
                  className="flex-1"
                  value={settlementNumber}
                  onChange={setSettlementNumber}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                The mobile-money number repayments settle to. Optional now — you can add it later.
              </p>
            </div>

            {/* City */}
            <Field
              id="city"
              label="City"
              icon={<MapPin className="w-3.5 h-3.5" />}
            >
              <Input
                id="city"
                placeholder="e.g. Hargeisa"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </Field>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-5">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ to: '/merchants' })}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-gradient-to-r from-primary to-primary/85 hover:from-primary/90 hover:to-primary/75 text-primary-foreground shadow-md"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Onboarding…
              </>
            ) : (
              'Onboard Merchant'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  icon,
  required,
  error,
  hint,
  children,
}: {
  id: string;
  label: string;
  icon?: React.ReactNode;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="flex items-center gap-1.5 text-sm">
        {icon}
        {label}
        {required ? <span className="text-red-500">*</span> : null}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-red-500">{error}</p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
